import { supabase } from "../../supabase/supabase";
import { scheduledCallService } from "./scheduledCallService";
import * as Sentry from "@sentry/react";

export interface ScheduledCall {
  id: string;
  lead_id: string;
  assistant_id: string;
  phone_number_id: string;
  status: string;
  scheduled_time: string;
  created_at: string;
}

export interface LeadFilters {
  organizationId?: string;
  limit?: number;
  offset?: number;
  cursor?: string;
  method?: 'cursor' | 'offset';
  searchQuery?: string;
  showScheduledOnly?: boolean;
}

export interface LeadsResponse {
  data: any[];
  pagination: {
    limit: number;
    method: 'offset' | 'cursor';
    hasMore: boolean;
    // Cursor-based fields
    nextCursor?: string;
    currentCursor?: string;
    estimatedTotal?: number;
    // Offset-based fields
    total?: number;
    offset?: number;
    page?: number;
    totalPages?: number;
    hasPrevious?: boolean;
  };
}

export const leadService = {
  /**
   * Get all leads for an organization (legacy - without pagination)
   */
  async getLeads(organizationId: string) {
    try {
      const { data, error } = await supabase
        .from("leads")
        .select("*")
        .eq("organization_id", organizationId)
        .order("created_at", { ascending: false });

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      Sentry.logger.error('Error getting leads', { 
        error,
        log_source: 'lead_service',
        organization_id: organizationId
      });
      return { data: null, error: error as Error };
    }
  },

  async getScheduledLeadIds(organizationId: string): Promise<string[]> {
    const { data, error } = await supabase
      .from("scheduled_calls")
      .select("lead_id")
      .eq("organization_id", organizationId)
      .eq("status", "scheduled");

    if (error) {
      Sentry.logger.error('Error fetching scheduled lead IDs', {
        error,
        log_source: 'lead_service',
        organization_id: organizationId
      });
      throw error;
    }
    const ids = [...new Set((data || []).map((row: { lead_id: string }) => row.lead_id))];
    return ids;
  },

  /**
   * Get leads with pagination support
   */
  async getLeadsWithPagination(filters: LeadFilters = {}): Promise<LeadsResponse> {
    const {
      organizationId,
      limit = 20,
      offset = 0,
      cursor,
      method = 'cursor',
      searchQuery,
      showScheduledOnly
    } = filters;

    try {
      if (!organizationId) {
        throw new Error("Organization ID is required");
      }

      let scheduledLeadIds: string[] | null = null;
      if (showScheduledOnly) {
        scheduledLeadIds = await leadService.getScheduledLeadIds(organizationId);
        if (scheduledLeadIds.length === 0) {
          return {
            data: [],
            pagination: {
              limit,
              method,
              hasMore: false,
              ...(method === 'cursor' ? {
                nextCursor: undefined,
                currentCursor: cursor,
                estimatedTotal: 0
              } : {
                total: 0,
                offset,
                page: 1,
                totalPages: 0,
                hasPrevious: false
              })
            }
          };
        }
      }

      let query = supabase
        .from("leads")
        .select("*", { count: 'exact' })
        .eq("organization_id", organizationId);

      if (showScheduledOnly && scheduledLeadIds) {
        query = query.in("id", scheduledLeadIds);
      }

      // Apply search filter if provided
      if (searchQuery && searchQuery.trim()) {
        const words = searchQuery
          .toLowerCase()
          .trim()
          .split(/\s+/)
          .filter((w) => w.length > 0);
        const escapeIlike = (s: string) => s.replace(/\\/g, "\\\\").replace(/%/g, "\\%").replace(/_/g, "\\_");
        const orClause = (word: string) => {
          const escaped = escapeIlike(word);
          return `first_name.ilike.%${escaped}%,last_name.ilike.%${escaped}%,email.ilike.%${escaped}%,phone_number.ilike.%${escaped}%`;
        };
        for (const word of words) {
          query = query.or(orClause(word));
        }
      }

      // Apply pagination
      if (method === 'cursor' && cursor) {
        query = query
          .lt('created_at', cursor)
          .order('created_at', { ascending: false })
          .limit(limit);
      } else if (method === 'offset') {
        query = query
          .order('created_at', { ascending: false })
          .range(offset, offset + limit - 1);
      } else {
        // Default cursor behavior (first page)
        query = query
          .order('created_at', { ascending: false })
          .limit(limit);
      }

      const { data, error, count } = await query;

      if (error) {
        throw error;
      }

      const leads = data || [];

      // Determine pagination info
      const hasMore = leads.length === limit;
      let nextCursor: string | undefined;
      
      if (method === 'cursor' && hasMore && leads.length > 0) {
        nextCursor = leads[leads.length - 1].created_at;
      }

      const pagination = {
        limit,
        method,
        hasMore,
        ...(method === 'cursor' ? {
          nextCursor,
          currentCursor: cursor,
          estimatedTotal: count ?? undefined
        } : {
          total: count ?? 0,
          offset,
          page: Math.floor(offset / limit) + 1,
          totalPages: count ? Math.ceil(count / limit) : 0,
          hasPrevious: offset > 0
        })
      };

      return {
        data: leads,
        pagination
      };
    } catch (error) {
      Sentry.logger.error('Error getting leads with pagination', { 
        error,
        log_source: 'lead_service',
        organization_id: organizationId,
        method: method,
        limit: limit
      });
      throw error;
    }
  },

  async getScheduledCallsForLead(leadId: string): Promise<ScheduledCall[]> {
    const { data, error } = await supabase
      .from("scheduled_calls")
      .select("*")
      .eq("lead_id", leadId)
      .eq("status", "scheduled")
      .order("scheduled_time", { ascending: true });

    if (error) {
      Sentry.logger.error('Error fetching scheduled calls for lead', { 
        error,
        log_source: 'lead_service',
        lead_id: leadId
      });
      throw error;
    }

    return data || [];
  },

  async getAllScheduledCalls(organizationId: string): Promise<ScheduledCall[]> {
    const { data, error } = await supabase
      .from("scheduled_calls")
      .select("*")
      .eq("organization_id", organizationId)
      .eq("status", "scheduled")
      .order("scheduled_time", { ascending: true });

    if (error) {
      Sentry.logger.error('Error fetching all scheduled calls', { 
        error,
        log_source: 'lead_service',
        organization_id: organizationId
      });
      throw error;
    }

    return data || [];
  },

  async scheduleCall(
    leadId: string,
    assistantId: string,
    phoneNumberId: string,
    scheduledTime: string,
    organizationId: string,
    userId: string
  ): Promise<ScheduledCall> {
    return scheduledCallService.createScheduledCall({
      lead_id: leadId,
      assistant_id: assistantId,
      phone_number_id: phoneNumberId,
      scheduled_time: scheduledTime,
    });
  },

  async rescheduleCall(
    scheduledCallId: string,
    scheduledTime: string
  ): Promise<ScheduledCall> {
    return scheduledCallService.rescheduleCall(scheduledCallId, scheduledTime);
  },

  async cancelScheduledCall(scheduledCallId: string): Promise<ScheduledCall> {
    return scheduledCallService.cancelScheduledCall(scheduledCallId);
  },
};
